import { useState } from 'react';

/* ── COLLAPSIBLE SECTION WRAPPER ── */
function CollapsibleSection({ id, num, title, children }: {
  id: string;
  num: string;
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);

  return (
    <section id={id}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 mb-0 pb-4 border-b border-gray-100 dark:border-white/5 group cursor-pointer"
      >
        <span className="text-xs font-black text-[#6366F1] tracking-widest font-mono flex-shrink-0">{num}</span>
        <div className="h-px flex-1 bg-gray-100 dark:bg-white/5"></div>
        <h2 className="text-lg font-extrabold uppercase tracking-[0.12em] text-gray-700 dark:text-[#94A3B8] text-right group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
          {title}
        </h2>
        <span className={`w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 dark:bg-white/5 group-hover:bg-[#6366F1]/10 transition-all flex-shrink-0 ${open ? 'rotate-180' : 'rotate-0'}`} style={{ transition: 'transform 0.3s ease, background 0.2s' }}>
          <i className="ri-arrow-down-s-line text-gray-400 dark:text-gray-500 group-hover:text-[#6366F1] text-base"></i>
        </span>
      </button>

      <div
        style={{
          maxHeight: open ? '9999px' : '0px',
          overflow: 'hidden',
          transition: open ? 'max-height 0.5s ease' : 'max-height 0.3s ease',
          opacity: open ? 1 : 0,
        }}
      >
        <div className="pt-6">
          {children}
        </div>
      </div>
    </section>
  );
}

export default function PrivacyContent() {
  return (
    <div className="bg-white dark:bg-[#0A0E1A] py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

          {/* ── SIDEBAR TOC ── */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24 space-y-6">
              <nav className="bg-white dark:bg-[#141828]/50 rounded-2xl p-6 border border-gray-200 dark:border-white/10 backdrop-blur-md">
                <h3 className="text-xs font-black text-gray-900 dark:text-white mb-5 uppercase tracking-[0.2em] font-mono border-b border-gray-100 dark:border-white/5 pb-4">
                  Contents
                </h3>
                <ul className="space-y-1.5 text-sm">
                  {[
                    { label: 'Information We Collect', id: 'section-1' },
                    { label: 'How We Use Your Data', id: 'section-2' },
                    { label: 'Legal Basis', id: 'section-3' },
                    { label: 'Storage & Security', id: 'section-4' },
                    { label: 'Data Retention', id: 'section-5' },
                    { label: 'Third-Party Services', id: 'section-6' },
                    { label: 'Your Rights', id: 'section-7' },
                    { label: 'Cookies', id: 'section-8' },
                    { label: "Children's Privacy", id: 'section-9' },
                    { label: 'Policy Changes', id: 'section-10' },
                    { label: 'Contact & Complaints', id: 'section-11' },
                  ].map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className="w-full text-left px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 text-xs font-bold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white cursor-pointer"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Privacy pledge card */}
              <div className="bg-[#6366F1]/5 dark:bg-[#6366F1]/10 rounded-2xl p-6 border border-[#6366F1]/20">
                <h3 className="text-xs font-black text-[#6366F1] dark:text-[#818CF8] mb-3 uppercase tracking-[0.2em] font-mono">
                  Our Pledge
                </h3>
                <ul className="space-y-2.5">
                  {[
                    'Never sell your data',
                    'Never show you ads',
                    'Ad-free products',
                    'No AI training on your data',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <span className="w-4 h-4 flex items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/20 flex-shrink-0">
                        <i className="ri-check-line text-emerald-500 text-[10px]"></i>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact card */}
              <div className="bg-gray-50 dark:bg-[#141828]/30 rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <h3 className="text-xs font-black text-gray-900 dark:text-white mb-4 uppercase tracking-[0.2em] font-mono border-b border-gray-100 dark:border-white/5 pb-3">
                  Privacy Contact
                </h3>
                <a
                  href="mailto:privacy@jobos.dev"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#6366F1] text-white text-[10px] font-black uppercase tracking-widest rounded-full hover:bg-[#4F46E5] transition-all shadow-lg whitespace-nowrap"
                >
                  <i className="ri-mail-line"></i>
                  privacy@jobos.dev
                </a>
              </div>
            </div>
          </aside>

          {/* ── MAIN CONTENT ── */}
          <div className="lg:col-span-9 space-y-10">

            {/* Intro */}
            <div>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                JobOS, operated by TechFraser, is committed to protecting your privacy in compliance with Canadian federal and provincial law.
              </p>

              <div className="bg-emerald-500/5 dark:bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex-shrink-0">
                    <i className="ri-shield-check-line text-emerald-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-bold mb-3">We take privacy seriously.</p>
                    <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-400">
                      <li className="flex items-center gap-2"><i className="ri-check-line text-emerald-500 text-xs"></i> We will never sell your personal data</li>
                      <li className="flex items-center gap-2"><i className="ri-check-line text-emerald-500 text-xs"></i> We will never show you ads based on your data</li>
                      <li className="flex items-center gap-2"><i className="ri-check-line text-emerald-500 text-xs"></i> Our products are ad-free</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Questions? Contact us at{' '}
                <a href="mailto:privacy@jobos.dev" className="text-[#6366F1] dark:text-[#818CF8] hover:underline font-medium">
                  privacy@jobos.dev
                </a>
              </p>

              <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-2xl p-6">
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-4">
                  JobOS ("we", "our", or "us") is a Canada-based technology company. This Privacy Policy explains what data we collect, how we use it, and your rights under applicable Canadian privacy law when you use our platform at app.jobos.dev and our browser extension.
                </p>
                <p className="text-gray-900 dark:text-white font-semibold text-sm mb-3">We operate in compliance with:</p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-start gap-2">
                    <i className="ri-government-line text-[#6366F1] mt-0.5 flex-shrink-0"></i>
                    <span><strong className="text-gray-900 dark:text-white">PIPEDA</strong> — the Personal Information Protection and Electronic Documents Act (federal)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-mail-check-line text-[#6366F1] mt-0.5 flex-shrink-0"></i>
                    <span><strong className="text-gray-900 dark:text-white">CASL</strong> — Canada's Anti-Spam Legislation (for all marketing and commercial electronic messages)</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
                  Where applicable, we also respect the requirements of Quebec's Act Respecting the Protection of Personal Information in the Private Sector (Law 25).
                </p>
              </div>
            </div>

            {/* Section 1 */}
            <CollapsibleSection id="section-1" num="01" title="Information We Collect">
              <div className="space-y-8">
                <SubSection title="1.1 Information You Provide Directly">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">When you create an account or use JobOS, you may provide:</p>
                  <BulletList items={[
                    { label: 'Account information', desc: 'name, email address, password (stored as a secure hash; never plaintext)' },
                    { label: 'Profile information', desc: 'first name, last name, phone number, location, LinkedIn URL, GitHub URL, portfolio URL, timezone' },
                    { label: 'Resume and career data', desc: 'work history, education, skills, certifications, professional summary' },
                    { label: 'Job application data', desc: 'job titles, company names, application statuses, salaries, notes, deadlines' },
                    { label: 'Target company data', desc: 'company names, domains, research notes, status tracking' },
                    { label: 'Contact and network data', desc: 'recruiter names, hiring manager details, follow-up notes' },
                    { label: 'Career goal data', desc: 'target job titles, salary ranges, target dates' },
                    { label: 'Interview data', desc: 'scheduled interview dates, availability windows, session notes' },
                  ]} />
                </SubSection>

                <SubSection title="1.2 Information Collected Automatically">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">When you use JobOS, we automatically collect:</p>
                  <BulletList items={[
                    { label: 'Usage data', desc: 'pages visited, features used, buttons clicked, session duration' },
                    { label: 'Technical data', desc: 'browser type, operating system, device type, screen resolution, IP address, approximate geographic location (province/city level)' },
                    { label: 'Performance data', desc: 'error logs, crash reports, page load times' },
                  ]} />
                </SubSection>

                <SubSection title="1.3 Information Collected via the Browser Extension">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">The JobOS Browser Extension collects data only when you actively use it:</p>
                  <BulletList items={[
                    { label: 'Job posting data', desc: 'job titles, company names, salaries, locations, and job descriptions extracted from job sites you visit' },
                    { label: 'ATS form data', desc: 'form field names detected on ATS platforms (Workday, Greenhouse, Lever, etc.) to enable autofill; we do not store ATS form data on our servers' },
                    { label: 'Professional profile data', desc: 'public profile information from pages you visit and choose to capture' },
                  ]} />
                  <div className="mt-5 bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 rounded-xl p-5">
                    <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">The extension does not:</p>
                    <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-400">
                      {['Read your passwords or financial information', 'Access pages unrelated to job search', 'Run in the background without your action', 'Collect data from pages you visit without triggering a capture'].map(t => (
                        <li key={t} className="flex items-center gap-2"><i className="ri-close-circle-line text-amber-500 text-xs flex-shrink-0"></i>{t}</li>
                      ))}
                    </ul>
                  </div>
                </SubSection>
              </div>
            </CollapsibleSection>

            {/* Section 2 */}
            <CollapsibleSection id="section-2" num="02" title="How We Use Your Information">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">We collect personal information only for the following identified purposes, as required under PIPEDA:</p>
              <BulletList items={[
                { label: 'Provide the service', desc: 'power your dashboard, store your applications, generate AI-tailored resumes, enable ATS autofill' },
                { label: 'Personalize your experience', desc: 'remember your preferences, track your job search progress' },
                { label: 'Improve JobOS', desc: 'understand which features are used, fix bugs, improve performance' },
                { label: 'Communicate with you', desc: 'send product updates, feature announcements, and account notifications. You will receive commercial electronic messages only with your express or implied consent, as required by CASL.' },
                { label: 'Provide AI features', desc: 'your resume data and job description text are sent to our AI provider (Anthropic) to generate tailored resume content and cover letters. See Section 6 for details.' },
                { label: 'Ensure security', desc: 'detect fraud, prevent abuse, enforce our Terms of Service' },
              ]} />
              <div className="mt-6 bg-red-500/5 dark:bg-red-500/10 border border-red-500/20 rounded-xl p-5">
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">We do not use your data to:</p>
                <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-400">
                  {['Sell to third parties', 'Show you advertisements', 'Share with employers or recruiters without your explicit consent', 'Train AI models without your explicit consent'].map(t => (
                    <li key={t} className="flex items-center gap-2"><i className="ri-close-circle-line text-red-500 text-xs flex-shrink-0"></i>{t}</li>
                  ))}
                </ul>
              </div>
            </CollapsibleSection>

            {/* Section 3 */}
            <CollapsibleSection id="section-3" num="03" title="Legal Basis for Collection and Use">
              <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                <p>Under applicable Canadian privacy law, we only collect, use, or disclose personal information with your knowledge and consent (express or implied), except where the law permits otherwise. The primary purposes for which we collect data are set out in Section 2 above and are communicated to you at or before the time of collection.</p>
                <p>You may withdraw consent at any time by contacting us at{' '}
                  <a href="mailto:privacy@jobos.dev" className="text-[#6366F1] dark:text-[#818CF8] hover:underline font-medium">privacy@jobos.dev</a>
                  , subject to legal or contractual restrictions. Withdrawing consent may limit our ability to provide certain services.
                </p>
              </div>
            </CollapsibleSection>

            {/* Section 4 */}
            <CollapsibleSection id="section-4" num="04" title="How We Store and Protect Your Data">
              <BulletList items={[
                { label: 'Infrastructure', desc: 'All data is stored on Supabase infrastructure, using PostgreSQL hosted on AWS' },
                { label: 'Encryption in transit', desc: 'All data in transit is encrypted via TLS 1.2 or higher' },
                { label: 'Password security', desc: 'Passwords are hashed using bcrypt — we never store plaintext passwords' },
                { label: 'Access control', desc: 'Database access is restricted to authorized personnel only' },
                { label: 'Security reviews', desc: 'We conduct periodic security reviews' },
              ]} />
              <div className="mt-5 bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-5">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Our infrastructure is primarily hosted in the US East (Virginia) region on AWS. Your data may be transferred to and stored in the United States. By using JobOS, you consent to this cross-border transfer.
                </p>
              </div>
            </CollapsibleSection>

            {/* Section 5 */}
            <CollapsibleSection id="section-5" num="05" title="Data Retention">
              <BulletList items={[
                { label: 'Active accounts', desc: 'we retain your data for as long as your account is active' },
                { label: 'Deleted accounts', desc: 'when you delete your account, we permanently delete your personal data within 30 days, except where retention is required by law' },
                { label: 'Backup retention', desc: 'deleted data may persist in encrypted backups for up to 90 days before being permanently purged' },
                { label: 'Usage logs', desc: 'anonymized usage analytics are retained for up to 24 months' },
              ]} />
            </CollapsibleSection>

            {/* Section 6 */}
            <CollapsibleSection id="section-6" num="06" title="Third-Party Services">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                JobOS uses the following third-party service providers to operate. Each has their own privacy policy governing their data practices.
              </p>
              <StyledTable
                headers={['Service', 'Purpose', 'Privacy Policy']}
                rows={[
                  ['Supabase', 'Database and authentication', <a href="https://supabase.com/privacy" className="text-[#6366F1] dark:text-[#818CF8] hover:underline" target="_blank" rel="noopener noreferrer">supabase.com/privacy</a>],
                  ['Anthropic Claude API', 'AI resume tailoring, cover letters, match scoring', <a href="https://anthropic.com/privacy" className="text-[#6366F1] dark:text-[#818CF8] hover:underline" target="_blank" rel="noopener noreferrer">anthropic.com/privacy</a>],
                  ['Cloudflare', 'DNS, email routing, DDoS protection', <a href="https://cloudflare.com/privacy" className="text-[#6366F1] dark:text-[#818CF8] hover:underline" target="_blank" rel="noopener noreferrer">cloudflare.com/privacy</a>],
                  ['Google Analytics', 'Usage analytics (anonymized)', <a href="https://policies.google.com/privacy" className="text-[#6366F1] dark:text-[#818CF8] hover:underline" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a>],
                  ['Resend', 'Transactional emails (auth, notifications)', <a href="https://resend.com/privacy" className="text-[#6366F1] dark:text-[#818CF8] hover:underline" target="_blank" rel="noopener noreferrer">resend.com/privacy</a>],
                  ['Clearbit', 'Company logo images via public API', <a href="https://clearbit.com/privacy" className="text-[#6366F1] dark:text-[#818CF8] hover:underline" target="_blank" rel="noopener noreferrer">clearbit.com/privacy</a>],
                ]}
              />
              <div className="mt-6 bg-[#6366F1]/5 dark:bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-2xl p-6">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <i className="ri-robot-line text-[#6366F1]"></i>
                  AI Processing (Anthropic)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  When you use AI-powered features (resume tailoring, cover letter generation, match scoring), the following data is sent to the Anthropic Claude API:
                </p>
                <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {['The job description text', 'Your resume / Master Profile content', 'Your professional summary'].map(t => (
                    <li key={t} className="flex items-center gap-2"><i className="ri-arrow-right-s-line text-[#6366F1] flex-shrink-0"></i>{t}</li>
                  ))}
                </ul>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This data is used solely to generate your tailored content. Per Anthropic's usage policies, API data is not used to train their models.
                </p>
              </div>
            </CollapsibleSection>

            {/* Section 7 */}
            <CollapsibleSection id="section-7" num="07" title="Your Rights">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Under applicable Canadian privacy law, you have the following rights regarding your personal information:</p>
              <StyledTable
                headers={['Right', 'Description', 'How to Exercise']}
                rows={[
                  ['Access', 'Request a copy of all personal data we hold about you', 'Email privacy@jobos.dev'],
                  ['Correction', 'Update or correct inaccurate information', 'Settings in-app, or email us'],
                  ['Deletion', 'Request permanent deletion of your account and all associated data', 'Settings → Delete Account'],
                  ['Portability / Export', 'Download your data in a portable format', 'Settings → Data → Export (CSV)'],
                  ['Withdraw Consent', 'Withdraw consent for processing at any time', 'Email privacy@jobos.dev'],
                  ['Opt-out (CASL)', 'Unsubscribe from commercial emails at any time', 'Unsubscribe link or Settings → Notifications'],
                  ['Complaint', 'File a complaint with the Office of the Privacy Commissioner of Canada', <a href="https://priv.gc.ca" className="text-[#6366F1] dark:text-[#818CF8] hover:underline" target="_blank" rel="noopener noreferrer">priv.gc.ca</a>],
                ]}
              />
              <div className="mt-5 bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-5">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  To exercise any of these rights, email{' '}
                  <a href="mailto:privacy@jobos.dev" className="text-[#6366F1] dark:text-[#818CF8] hover:underline font-medium">privacy@jobos.dev</a>{' '}
                  with the subject line "Data Request — [Your Request Type]". We will respond as promptly as reasonably possible.
                </p>
              </div>
            </CollapsibleSection>

            {/* Section 8 */}
            <CollapsibleSection id="section-8" num="08" title="Cookies">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                JobOS uses cookies to provide and improve our service. You will be asked for consent for non-essential cookies when you first visit our site, consistent with Canadian privacy requirements.
              </p>
              <StyledTable
                headers={['Cookie', 'Type', 'Purpose', 'Duration']}
                rows={[
                  [<code className="text-xs font-mono bg-gray-100 dark:bg-white/5 px-1.5 py-0.5 rounded">sb-auth-token</code>, 'Essential', 'Supabase authentication session', 'Session'],
                  [<code className="text-xs font-mono bg-gray-100 dark:bg-white/5 px-1.5 py-0.5 rounded">jobos_theme</code>, 'Preference', 'Stores your dark/light mode preference', '1 year'],
                  [<code className="text-xs font-mono bg-gray-100 dark:bg-white/5 px-1.5 py-0.5 rounded">_ga, _gid</code>, 'Analytics (opt-in)', 'Google Analytics usage tracking', '2 years / 24 hours'],
                ]}
              />
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">Disabling analytics cookies does not affect product functionality.</p>
            </CollapsibleSection>

            {/* Section 9 */}
            <CollapsibleSection id="section-9" num="09" title="Children's Privacy">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                JobOS is not intended for use by anyone under the age of 16. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, contact us at{' '}
                <a href="mailto:privacy@jobos.dev" className="text-[#6366F1] dark:text-[#818CF8] hover:underline font-medium">privacy@jobos.dev</a>{' '}
                and we will delete it promptly.
              </p>
            </CollapsibleSection>

            {/* Section 10 */}
            <CollapsibleSection id="section-10" num="10" title="Changes to This Policy">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">When we make significant changes, we will:</p>
              <BulletList items={[
                { label: 'Update the date', desc: 'Update the "Last Updated" date at the top of this page' },
                { label: 'Email notification', desc: 'Send a notification email to registered users' },
                { label: 'In-app banner', desc: 'Display an in-app banner for 14 days' },
              ]} />
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                Your continued use of JobOS after changes take effect constitutes acceptance of the updated policy.
              </p>
            </CollapsibleSection>

            {/* Section 11 */}
            <CollapsibleSection id="section-11" num="11" title="Contact & Complaints">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                If you have questions, concerns, or a complaint about how we handle your personal information, please reach out:
              </p>
              <StyledTable
                headers={['Channel', 'Details']}
                rows={[
                  ['Privacy / Data Requests', <a href="mailto:privacy@jobos.dev" className="text-[#6366F1] dark:text-[#818CF8] hover:underline">privacy@jobos.dev</a>],
                  ['General Inquiries', <a href="mailto:hello@jobos.dev" className="text-[#6366F1] dark:text-[#818CF8] hover:underline">hello@jobos.dev</a>],
                  ['Support', <a href="mailto:support@jobos.dev" className="text-[#6366F1] dark:text-[#818CF8] hover:underline">support@jobos.dev</a>],
                  ['Website', <a href="https://jobos.dev" className="text-[#6366F1] dark:text-[#818CF8] hover:underline" target="_blank" rel="noopener noreferrer">jobos.dev</a>],
                ]}
              />
              <div className="mt-5 bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-5">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  We are committed to resolving privacy concerns promptly. If you are not satisfied with our response, you have the right to contact the{' '}
                  <strong className="text-gray-900 dark:text-white">Office of the Privacy Commissioner of Canada</strong> at{' '}
                  <a href="https://priv.gc.ca" className="text-[#6366F1] dark:text-[#818CF8] hover:underline font-medium" target="_blank" rel="noopener noreferrer">priv.gc.ca</a>.
                </p>
              </div>
            </CollapsibleSection>

            {/* Footer note */}
            <div className="pt-8 border-t border-gray-100 dark:border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                <strong className="text-gray-600 dark:text-gray-400">JobOS by TechFraser</strong> — Privacy Policy — Effective March 5, 2026
              </p>
              <a
                href="mailto:privacy@jobos.dev"
                className="inline-flex items-center gap-2 text-xs text-[#6366F1] dark:text-[#818CF8] hover:underline font-medium whitespace-nowrap"
              >
                <i className="ri-mail-line"></i>
                privacy@jobos.dev
              </a>
            </div>

            {/* See also cross-link */}
            <div className="mt-10">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 font-mono mb-4">See Also</p>
              <a
                href="/terms"
                className="flex items-center gap-5 p-5 rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#141828]/40 hover:border-[#6366F1]/40 hover:bg-[#6366F1]/5 dark:hover:bg-[#6366F1]/10 transition-all group cursor-pointer"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0">
                  <i className="ri-file-text-line text-[#6366F1] text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-[#6366F1] transition-colors">
                    Terms of Service
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Read our full Terms of Service — usage rules, subscription terms, liability, and more.
                  </div>
                </div>
                <i className="ri-arrow-right-line text-[#6366F1] text-lg opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"></i>
              </a>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SHARED SUB-COMPONENTS ── */

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-black text-gray-900 dark:text-white mb-4 uppercase tracking-wider">{title}</h3>
      {children}
    </div>
  );
}

function BulletList({ items }: { items: { label: string; desc: string }[] }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item.label} className="flex items-start gap-3">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#6366F1] flex-shrink-0"></span>
          <span className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            <strong className="text-gray-900 dark:text-white">{item.label}</strong> — {item.desc}
          </span>
        </li>
      ))}
    </ul>
  );
}

function StyledTable({ headers, rows }: { headers: string[]; rows: (React.ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-white/10">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-[#141828]/80">
            {headers.map((h) => (
              <th key={h} className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-white/10">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-transparent' : 'bg-gray-50/50 dark:bg-white/[0.02]'}>
              {row.map((cell, j) => (
                <td key={j} className="px-5 py-3.5 text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-white/5 last:border-b-0 align-top">
                  {j === 0 ? <span className="font-semibold text-gray-900 dark:text-white">{cell}</span> : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
